/**
 * A list that also holds a second object (concepts.yaml `list_includes`, the contract's one contained exception to
 * "one object per list": the Orders list also holds Tharstern orders, the Quotes list Tharstern estimates).
 *
 * Pure reading of the manifest and of a row: which object a row is a record of, which filters and rows the list
 * shows, what the person is told when a filter or queue is leaving the second object's rows out, and the filters'
 * defaults. Everything still runs server-side; this only shapes what the screen draws.
 */
import type {
  ChildRow,
  ManifestList,
  ManifestListFilter,
  ObjectId,
} from "./contract";
import { withFilter, type FilterValue, type ListQuery } from "./list-query";
import type { ManifestIndex } from "./manifest";

/** The list route's query param that picks one kind of row (absent: both). */
export const KIND_PARAM = "kind";

/** A row's record: the object it opens and the key that object is read by. */
export interface RowTarget {
  object: ObjectId;
  key: string;
  /** Whether the row is the second object's, not the list's own. */
  isIncluded: boolean;
}

/**
 * The object a list row is a record of: the list's own object and key field, except on a list that holds a second
 * object, where the row's `kind` says which. A row without the identifier gets an empty key, which links nowhere.
 */
export function rowTarget(
  index: ManifestIndex,
  ownObject: ObjectId,
  list: ManifestList,
  row: ChildRow,
): RowTarget {
  const includes = list.includes;
  if (includes && row[KIND_PARAM] === includes.kind) {
    const field = index.objectOf(includes.object).identifier.field;
    return {
      object: includes.object,
      key: String(row[field] ?? ""),
      isIncluded: true,
    };
  }
  return {
    object: ownObject,
    key: String(row[list.key_field] ?? ""),
    isIncluded: false,
  };
}

/**
 * The column a row's link sits on. A list's own row links on its key column; the second object's row has none there
 * (an order has no order number), so it links on its first identifier column that has a value (a Tharstern sales order
 * number, else a Tharstern job number).
 */
export function linkField(list: ManifestList, row: ChildRow): string | null {
  const includes = list.includes;
  if (!includes || row[KIND_PARAM] !== includes.kind) return list.key_field;
  const column = list.columns.find(
    (c) =>
      c.field !== list.key_field &&
      c.kind === "identifier" &&
      row[c.field] != null &&
      row[c.field] !== "",
  );
  return column?.field ?? null;
}

/**
 * The filters the screen draws. While the second object is switched off for the person the list answers its own rows
 * only, so the Kind filter and the filters that narrow only the second object's rows are not drawn.
 */
export function shownFilters(
  list: ManifestList,
  isIncludedEnabled: boolean,
): ManifestListFilter[] {
  if (!list.includes || isIncludedEnabled) return list.filters;
  return list.filters.filter(
    (f) => f.param !== KIND_PARAM && !f.included_kind_only,
  );
}

/** The value a filter shows: the one set, else its default (a switch that is on until it is turned off). */
export function shownFilterValue(
  filter: ManifestListFilter,
  query: ListQuery,
): FilterValue | undefined {
  const set = query.filters[filter.param];
  if (set !== undefined) return set;
  return filter.default === null || filter.default === undefined
    ? undefined
    : String(filter.default);
}

/** The values a select filter has chosen: one for an enum, one or more for a many-valued filter. */
export function chosenValues(value: FilterValue | undefined): string[] {
  if (value === undefined) return [];
  return Array.isArray(value) ? value : [value];
}

/**
 * What a many-valued select reads when more than one value is chosen ("Route: Standard, Express"); a single value is
 * read by its own option, so null. A value the filter has no option for reads as it is.
 */
export function manyChosenLabel(
  filter: ManifestListFilter,
  value: FilterValue | undefined,
): string | null {
  const values = chosenValues(value);
  if (values.length < 2) return null;
  const names = new Map(filter.options);
  return `${filter.label}: ${values.map((v) => names.get(v) ?? v).join(", ")}`;
}

/**
 * The query after the person picks `value` on a filter that has a default: picking the default again clears the
 * param, so the URL and the request leave a default out.
 */
export function withDefaultedFilter(
  query: ListQuery,
  filter: ManifestListFilter,
  value: string,
): ListQuery {
  const isDefault =
    filter.default !== null &&
    filter.default !== undefined &&
    String(filter.default) === value;
  return withFilter(query, filter.param, isDefault ? null : value);
}

/**
 * Whether every row the list can show is the list's own: it holds no second object, `kind` picks the own kind, or a
 * queue or a filter that only the own kind has is set (the list then builds no second-object rows at all).
 */
export function isOwnKindOnly(list: ManifestList, query: ListQuery): boolean {
  const includes = list.includes;
  if (!includes) return true;
  const kind = query.filters[KIND_PARAM];
  if (kind !== undefined) return kind === includes.own_kind;
  return list.filters.some(
    (f) => f.own_kind_only && query.filters[f.param] !== undefined,
  );
}

/** The same list restricted to its own rows (what Work through these walks on a list that holds a second object). */
export function ownKindQuery(
  list: ManifestList,
  query: ListQuery,
): ListQuery | null {
  return list.includes
    ? withFilter(query, KIND_PARAM, list.includes.own_kind)
    : null;
}

/**
 * The line the list shows above its rows while a queue or a filter that only the own kind has is leaving the second
 * object's rows out, or null when nothing is. Said only on the All view: choosing a kind is the person's own choice.
 */
export function leftOutNote(
  list: ManifestList,
  query: ListQuery,
  includedPlural: string,
): string | null {
  if (!list.includes || query.filters[KIND_PARAM] !== undefined) return null;
  const queue = list.queues.find((q) => q.id === query.queue);
  if (queue)
    return `${queue.label} queue chosen: the list will leave out ${includedPlural} until the queue is cleared.`;
  const filter = list.filters.find(
    (f) => f.own_kind_only && query.filters[f.param] !== undefined,
  );
  return filter
    ? `${filter.label} set: the list will leave out ${includedPlural} until ${filter.label} is cleared.`
    : null;
}
